import React, { useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import html2canvas from "html2canvas";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

/**
 * Nicotine & Sugar confirmation dialog shown before saving a shipment request.
 *
 * Props:
 *  open      – boolean
 *  onClose   – called when user cancels
 *  onConfirm – called when user confirms and proceeds with save
 *  soData    – the sales order object
 *  poData    – the purchase order object
 */
const NicotineSugarSummary = ({ open, onClose, onConfirm, soData, poData }) => {
  const tableRef = useRef(null);

  const fmt = (v) =>
    v != null && v !== "" ? Number(v).toFixed(4) : "-";

  const masterNic =
    soData?.offer?.item?.nicotine ?? poData?.offer?.item?.nicotine;
  const masterSug =
    soData?.offer?.item?.sugar ?? poData?.offer?.item?.sugar;

  const isDiff = (value, ref) => {
    if (value == null || value === "" || ref == null || ref === "") return null;
    return Math.abs(Number(value) - Number(ref)) > 0.00001;
  };

  const valueCellSx = (value, ref) => {
    const differs = isDiff(value, ref);
    return {
      border: "1px solid #dde7ef",
      padding: "4px 10px",
      fontSize: "0.8rem",
      textAlign: "center",
      fontWeight: differs ? 700 : 400,
      color: differs === true ? "#b71c1c" : differs === false ? "#1b5e20" : "#444",
      backgroundColor:
        differs === true ? "#ffebee" : differs === false ? "#e8f5e9" : "transparent",
    };
  };

  const refCellSx = {
    border: "1px solid #dde7ef",
    padding: "4px 10px",
    fontSize: "0.78rem",
    textAlign: "center",
    color: "#607d8b",
    backgroundColor: "#f0f4f7",
  };

  const labelSx = {
    border: "1px solid #dde7ef",
    padding: "5px 14px",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#2c3e50",
    backgroundColor: "#f7fafc",
    whiteSpace: "nowrap",
  };

  const sectionSx = {
    border: "1px solid #b0c8dc",
    padding: "5px 14px",
    fontSize: "0.73rem",
    fontWeight: 700,
    color: "#ffffff",
    backgroundColor: "#1976d2",
    letterSpacing: "0.04em",
  };

  const SectionRow = ({ step, label }) => (
    <TableRow>
      <TableCell colSpan={5} sx={sectionSx}>
        {step} &nbsp;·&nbsp; {label}
      </TableCell>
    </TableRow>
  );

  const DataRow = ({ label, nicotine, sugar }) => (
    <TableRow>
      <TableCell sx={labelSx}>{label}</TableCell>
      <TableCell sx={valueCellSx(nicotine, masterNic)}>{fmt(nicotine)}</TableCell>
      <TableCell sx={refCellSx}>{fmt(masterNic)}</TableCell>
      <TableCell sx={valueCellSx(sugar, masterSug)}>{fmt(sugar)}</TableCell>
      <TableCell sx={refCellSx}>{fmt(masterSug)}</TableCell>
    </TableRow>
  );

  const EmptyRow = ({ label }) => (
    <TableRow>
      <TableCell sx={labelSx}>{label}</TableCell>
      <TableCell
        colSpan={4}
        sx={{
          border: "1px solid #dde7ef",
          textAlign: "center",
          color: "#b0bec5",
          fontSize: "0.76rem",
          fontStyle: "italic",
        }}
      >
        - not available -
      </TableCell>
    </TableRow>
  );

  const hasValue = (obj, ...keys) =>
    keys.some((k) => obj?.[k] != null && obj?.[k] !== "");

  // ── Excel export data ──────────────────────────────────────────────────
  const buildExcelRows = () => {
    const rows = [];
    const sec = (label) =>
      rows.push({ Source: `▶  ${label}`, Nicotine: "", "Master Nicotine": "", Sugar: "", "Master Sugar": "" });
    const row = (label, nic, sug) =>
      rows.push({
        Source: label,
        Nicotine: nic != null && nic !== "" ? Number(nic) : "",
        "Master Nicotine": masterNic != null ? Number(masterNic) : "",
        Sugar: sug != null && sug !== "" ? Number(sug) : "",
        "Master Sugar": masterSug != null ? Number(masterSug) : "",
      });

    sec("1 · Master Item");
    const item = soData?.offer?.item ?? poData?.offer?.item;
    if (item) row(`Item #${item.id} · ${item.buyingGrade ?? ""}`, masterNic, masterSug);
    else row("No item data", "", "");

    sec("2 · Offer Values (Sales / Purchase)");
    soData?.offer ? row(`Customer Offer #${soData.offer.id}`, soData.offer.salesNicotine, soData.offer.salesSugar) : row("Customer Offer - not selected", "", "");
    sec("3 · Samples");
    soData?.offer?.firstSample ? row(`Customer Offer Sample #${soData.offer.firstSample.id}`, soData.offer.firstSample.salesNicotine, soData.offer.firstSample.salesSugar) : row("Customer Offer Sample - none", "", "");
    soData?.offer?.secondSample ? row(`Customer Offer Trial Sample #${soData.offer.secondSample.id}`, soData.offer.secondSample.salesNicotine, soData.offer.secondSample.salesSugar) : row("Customer Offer Trial Sample - none", "", "");
    soData?.firstSample ? row(`SO Redrawn Sample #${soData.firstSample.id}`, soData.firstSample.salesNicotine, soData.firstSample.salesSugar) : row("SO Redrawn Sample - none", "", "");
    poData?.offer?.firstSample ? row(`Vendor Offer Sample #${poData.offer.firstSample.id}`, poData.offer.firstSample.salesNicotine, poData.offer.firstSample.salesSugar) : row("Vendor Offer Sample - none", "", "");
    poData?.offer?.secondSample ? row(`Vendor Offer Trial Sample #${poData.offer.secondSample.id}`, poData.offer.secondSample.salesNicotine, poData.offer.secondSample.salesSugar) : row("Vendor Offer Trial Sample - none", "", "");
    poData?.firstSample ? row(`PO Redrawn Sample #${poData.firstSample.id}`, poData.firstSample.salesNicotine, poData.firstSample.salesSugar) : row("PO Redrawn Sample - none", "", "");

    sec("4 · Packaging Labels");
    hasValue(soData, "packagingNicotine", "packagingSugar") ? row(`SO #${soData?.id} · Packaging`, soData.packagingNicotine, soData.packagingSugar) : row("SO Packaging - not set", "", "");
    hasValue(poData, "packagingNicotine", "packagingSugar") ? row(`PO #${poData?.id} · Packaging`, poData.packagingNicotine, poData.packagingSugar) : row("PO Packaging - not set", "", "");

    return rows;
  };

  const handleDownloadExcel = () => {
    const rows = buildExcelRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 44 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1976D2" } },
      alignment: { horizontal: "center" },
      border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } },
    };
    ["A1", "B1", "C1", "D1", "E1"].forEach((cell) => {
  if (ws[cell]) ws[cell].s = headerStyle;
});

const sectionStyle = {
  font: { bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "6fa9e3" } },
  alignment: { horizontal: "left" },
  border: {
    top: { style: "thin" },
    bottom: { style: "thin" },
    left: { style: "thin" },
    right: { style: "thin" },
  },
};

rows.forEach((r, index) => {
  const excelRowNumber = index + 2; // JSON header is row 1

  if (String(r.Source || "").startsWith("▶")) {
    ["A", "B", "C", "D", "E"].forEach((col) => {
      const cellRef = `${col}${excelRowNumber}`;

      if (!ws[cellRef]) {
        ws[cellRef] = { t: "s", v: "" };
      }

      ws[cellRef].s = sectionStyle;
    });
  }
});
    const wb = { Sheets: { "Nic-Sugar Summary": ws }, SheetNames: ["Nic-Sugar Summary"] };
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `nic_sugar_summary_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleDownloadImage = async () => {
    if (!tableRef.current) return;
    const canvas = await html2canvas(tableRef.current, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
    canvas.toBlob((blob) => {
      FileSaver.saveAs(blob, `nic_sugar_summary_${new Date().toISOString().slice(0, 10)}.png`);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* ── Title ─────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #b71c1c 0%, #e53935 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 1.5,
          px: 2.5,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 28, color: "#ffe082", flexShrink: 0 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Nicotine &amp; Sugar Values - Please Review Before Saving
          </Typography>
          <Typography variant="caption" sx={{ color: "#ffcdd2", fontSize: "0.72rem" }}>
            Red&nbsp;=&nbsp;differs from master item &nbsp;|&nbsp; Green&nbsp;=&nbsp;matches master item
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* ── Download toolbar ──────────────────────────────────── */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: 2, py: 1, backgroundColor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}
        >
          <Button size="small" variant="outlined" startIcon={<ImageIcon />}
            onClick={handleDownloadImage}
            sx={{ textTransform: "none", fontSize: "0.76rem" }}>
            Download as Image
          </Button>
          <Button size="small" variant="outlined" startIcon={<TableChartIcon />}
            onClick={handleDownloadExcel}
            sx={{ textTransform: "none", fontSize: "0.76rem", color: "#2e7d32", borderColor: "#2e7d32" }}>
            Download as Excel
          </Button>
        </Stack>

        {/* ── Table ─────────────────────────────────────────────── */}
        <Box ref={tableRef} sx={{ px: 2, pt: 1.5, pb: 1, backgroundColor: "#fff" }}>
          <TableContainer>
            <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...sectionSx, backgroundColor: "#37474f", width: "34%", fontSize: "0.76rem" }}>Source</TableCell>
                  <TableCell sx={{ ...sectionSx, backgroundColor: "#37474f", width: "16.5%", textAlign: "center", fontSize: "0.76rem" }}>Nicotine</TableCell>
                  <TableCell sx={{ ...sectionSx, backgroundColor: "#546e7a", width: "16.5%", textAlign: "center", fontSize: "0.76rem" }}>↑ Master Nic</TableCell>
                  <TableCell sx={{ ...sectionSx, backgroundColor: "#37474f", width: "16.5%", textAlign: "center", fontSize: "0.76rem" }}>Sugar</TableCell>
                  <TableCell sx={{ ...sectionSx, backgroundColor: "#546e7a", width: "16.5%", textAlign: "center", fontSize: "0.76rem" }}>↑ Master Sugar</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* 1 · Master Item */}
                <SectionRow step="1" label="Master Item (Physical Leaf Characteristics)" />
                {(soData?.offer?.item || poData?.offer?.item) ? (
                  <DataRow
                    label={`Item #${soData?.offer?.item?.id ?? poData?.offer?.item?.id ?? "-"}  ·  ${soData?.offer?.item?.buyingGrade ?? poData?.offer?.item?.buyingGrade ?? ""}`}
                    nicotine={masterNic}
                    sugar={masterSug}
                  />
                ) : <EmptyRow label="No item data available" />}

                {/* 2 · Offer Values */}
                <SectionRow step="2" label="Offer Values (Sales / Purchase)" />
                {soData?.offer
                  ? <DataRow label={`Customer Offer #${soData.offer.id ?? "-"}  ·  Sales Offer`} nicotine={soData.offer.salesNicotine} sugar={soData.offer.salesSugar} />
                  : <EmptyRow label="Customer Offer - not selected" />}

                {/* 3 · Samples */}
                <SectionRow step="3" label="Samples" />
                 {soData?.offer?.firstSample
                  ? <DataRow label={`Customer Offer #${soData.offer.firstSample.id}  ·  Offer Sample`} nicotine={soData.offer.firstSample.salesNicotine} sugar={soData.offer.firstSample.salesSugar} />
                  : <EmptyRow label="Customer Offer Offer Sample - none" />}
                {soData?.offer?.secondSample
                  ? <DataRow label={`Customer Offer #${soData.offer.secondSample.id}  ·  Trial Sample`} nicotine={soData.offer.secondSample.salesNicotine} sugar={soData.offer.secondSample.salesSugar} />
                  : <EmptyRow label="Customer Offer Trial Sample - none" />}
                {soData?.firstSample
                  ? <DataRow label={`SO Sample #${soData.firstSample.id}  ·  Redrawn Sample`} nicotine={soData.firstSample.salesNicotine} sugar={soData.firstSample.salesSugar} />
                  : <EmptyRow label="SO Sample - none" />}
                 {poData?.offer?.firstSample
                  ? <DataRow label={`Vendor Offer #${poData.offer.firstSample.id}  ·  Offer Sample`} nicotine={poData.offer.firstSample.salesNicotine} sugar={poData.offer.firstSample.salesSugar} />
                  : <EmptyRow label="Vendor Offer Offer Sample - none" />}
                {poData?.offer?.secondSample
                  ? <DataRow label={`Vendor Offer #${poData.offer.secondSample.id}  ·  Trial Sample`} nicotine={poData.offer.secondSample.salesNicotine} sugar={poData.offer.secondSample.salesSugar} />
                  : <EmptyRow label="Vendor Offer Trial Sample - none" />}
                {poData?.firstSample
                  ? <DataRow label={`PO Sample #${poData.firstSample.id}  ·  Redrawn Sample`} nicotine={poData.firstSample.salesNicotine} sugar={poData.firstSample.salesSugar} />
                  : <EmptyRow label="PO Sample - none" />}

                {/* 4 · Packaging */}
                <SectionRow step="4" label="Packaging Labels (declared on order)" />
                {hasValue(soData, "packagingNicotine", "packagingSugar")
                  ? <DataRow label={`SO #${soData?.id ?? "-"}  ·  Packaging`} nicotine={soData?.packagingNicotine} sugar={soData?.packagingSugar} />
                  : <EmptyRow label="SO Packaging - not set" />}
                {hasValue(poData, "packagingNicotine", "packagingSugar")
                  ? <DataRow label={`PO #${poData?.id ?? "-"}  ·  Packaging`} nicotine={poData?.packagingNicotine} sugar={poData?.packagingSugar} />
                  : <EmptyRow label="PO Packaging - not set" />}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      {/* ── Actions ───────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          backgroundColor: "#fafafa",
          borderTop: "1px solid #e0e0e0",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" sx={{ color: "#c62828", fontWeight: 600, fontSize: "0.8rem" }}>
          ⚠&nbsp; Please verify these values before proceeding.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onConfirm} sx={{ textTransform: "none", fontWeight: 700 }}>
            I have reviewed - Continue
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default NicotineSugarSummary;
